/**
 * React hooks for user data management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '@/services';
import type { User, UserCreateRequest, UserUpdateRequest, PaginationOptions, UserStatistics, UserFilterOptions } from '@/types';

const USER_QUERY_KEYS = {
  all: ['users'] as const,
  lists: () => [...USER_QUERY_KEYS.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...USER_QUERY_KEYS.lists(), { filters }] as const,
  details: () => [...USER_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...USER_QUERY_KEYS.details(), id] as const,
};

/**
 * Hook to fetch users with pagination and filtering
 */
export function useUsers(options?: PaginationOptions, filters?: UserFilterOptions) {
  return useQuery({
    queryKey: [...USER_QUERY_KEYS.lists(), options, filters],
    queryFn: () => userService.getUsers(options, filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a specific user
 */
export function useUser(id: string, enabled = true) {
  return useQuery({
    queryKey: USER_QUERY_KEYS.detail(id),
    queryFn: () => userService.getUser(id),
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a new user
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UserCreateRequest) => userService.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: [...USER_QUERY_KEYS.all, 'statistics'] });
    },
  });
}

/**
 * Hook to update an existing user
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserUpdateRequest }) =>
      userService.updateUser(id, data),
    onSuccess: (updatedUser: User) => {
      queryClient.setQueryData(
        USER_QUERY_KEYS.detail(updatedUser.id),
        updatedUser
      );
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: [...USER_QUERY_KEYS.all, 'statistics'] });
    },
  });
}

/**
 * Hook to delete a user
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: (_: void, deletedId: string) => {
      queryClient.removeQueries({ queryKey: USER_QUERY_KEYS.detail(deletedId) });
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: [...USER_QUERY_KEYS.all, 'statistics'] });
    },
  });
}

/**
 * Hook to fetch user statistics with optional filtering
 */
export function useUserStatistics(filters?: UserFilterOptions) {
  return useQuery({
    queryKey: [...USER_QUERY_KEYS.all, 'statistics', filters],
    queryFn: () => userService.getUserStatistics(filters),
    staleTime: 1 * 60 * 1000, // 1 minute - cache statistics for shorter time to see updates faster
  });
}
